import { getRuntimeConfig } from "@/app/config/runtime-config";
import { parseDomainLegacyOwned } from "@/core/write-authority/domain-legacy-owned";
import type { components } from "@/types/generated/invitation-api";
import { isPasswordPolicyDescriptor } from "@/features/first-credential/first-credential-client";
import { MAX_RETRY_AFTER_SECONDS, parseRetryAfterSeconds } from "./credential-recovery-client";

/**
 * Thin client over the CLMS-113 public registration-invitation contract.
 * Types derive from src/types/generated/invitation-api.ts (generated via
 * `pnpm run spec:types:invitation` from the checked-in OpenAPI). Served by
 * clm-web-service-auth on the standard realm; the base URL is the standard
 * `authBaseUrl` from runtime config. The bearer proof travels only in the
 * JSON request body — never in a path, query string, or header.
 */

type Schemas = components["schemas"];

export type RegistrationVariant = Schemas["RegistrationVariant"];
export type InvitationLookupRequest = Schemas["InvitationLookupRequest"];
export type PublicSafeOutcome = Schemas["PublicSafeOutcome"];
export type PublicInvalidRequest = Schemas["PublicInvalidRequest"];
export type PublicRateLimited = Schemas["PublicRateLimited"];
export type PublicDependencyUnavailable = Schemas["PublicDependencyUnavailable"];
export type CompletionSucceeded =
  | Schemas["DirectFellowCompletionSucceeded"]
  | MemberCompletionSucceeded;
/**
 * Manual additive widening (FR-045/FR-046): the CLMS-117 auth-service
 * revision adds the optional non-secret `handoffId` (status-API key) and
 * the optional SECRET `accessToken` (handoff-scoped bearer JWT authorizing
 * only the registration-financial status/receipt-copy endpoints) to the
 * Member success `financialHandoff`; the checked-in invitation OpenAPI does
 * not carry them yet. The accessToken is deposited into the module-memory
 * token holder at the completion boundary and MUST never be copied into
 * React state, storage, the URL, or logs.
 */
export type MemberCompletionSucceeded = Omit<
  Schemas["MemberCompletionSucceeded"],
  "financialHandoff"
> & {
  financialHandoff: Schemas["MemberCompletionSucceeded"]["financialHandoff"] & {
    handoffId?: string;
    accessToken?: string;
  };
};
export type ValidationFailed = Schemas["ValidationFailed"];
export type EditableProfile = Schemas["EditableProfile"];
/**
 * Fellow detail shape with `birthYearRangeReference` widened from the
 * generated contract-pinned enum to `string | null`: the SPA submits only
 * references from the server's optionCatalogs.birthYearRangeReferences
 * catalog, which is the runtime authority for the closed vocabulary.
 */
export type DirectFellowDetails = Omit<
  Schemas["DirectFellowDetails"],
  "birthYearRangeReference"
> & {
  birthYearRangeReference?: string | null;
};
export type PersonNamePrefill = Schemas["PersonNamePrefill"];
export type PublicationPreferences = Schemas["PublicationPreferences"];
export type DeliveryPreference = Schemas["DeliveryPreference"];
export type ReferenceOption = Schemas["ReferenceOption"];
export type DirectFellowOptionCatalogs = Schemas["DirectFellowOptionCatalogs"];
export type MemberOptionCatalogs = Schemas["MemberOptionCatalogs"];

/** Union of the variant-scoped closed option catalogs a ready lookup carries. */
export type VariantOptionCatalogs = DirectFellowOptionCatalogs | MemberOptionCatalogs;

/**
 * Contract 429s carry a `Retry-After` header (T054/T055); the client
 * surfaces it as a non-contract-body hint so the UI can hold its retry
 * affordance for the advertised window.
 */
export type RateLimitedResult = PublicRateLimited & { retryAfterSeconds?: number };

/**
 * A 503 `dependency_unavailable` MAY carry `Retry-After` (UI contract L168):
 * for a member `finalize` the SPA keeps the attempt pending and re-sends
 * `finalize` under the same key after the interval instead of dropping to
 * the support surface.
 */
export type DependencyUnavailableResult = PublicDependencyUnavailable & {
  retryAfterSeconds?: number;
};

export type IdempotencyConflict = Schemas["InvitationIdempotencyConflict"];

/**
 * CLMS-268 T042 (FR-014): the completion 409 `DOMAIN_LEGACY_OWNED`, i.e.
 * persons is legacy-owned and the auth service refused the completion before
 * any state change. The wire body is the flat write-authority error (no
 * `outcome`, no `correlationId`); the client reduces it to this payload-free
 * marker so the invitee page can show its own "registration is paused"
 * refusal and the staff-facing `message` / legacy pointer never reach it.
 */
export interface DomainLegacyOwnedResult {
  outcome: "domain_legacy_owned";
}

/**
 * Lookup ready shapes. The generated `LookupReadyBase.prefill` and
 * `optionCatalogs` are the sparse `Record<string, never>` (contract
 * "variant-specific" objects), which reject any concrete value; the variants
 * are recomposed here with the generated variant prefill and option-catalog
 * sub-schemas narrowed in.
 */
type LookupReadyEnvelope = Omit<
  Schemas["LookupReadyBase"],
  "prefill" | "registrationVariant" | "optionCatalogs"
>;

export type DirectFellowLookupReady = LookupReadyEnvelope & {
  registrationVariant: "direct_fellow";
  optionCatalogs: DirectFellowOptionCatalogs;
  prefill: PersonNamePrefill & {
    staffCounsel?: {
      approved?: boolean;
      officeReference?: string | null;
      officeName?: string | null;
    };
    publicationPreferences?: Schemas["PublicationPreferences"];
  };
};

export type MemberLookupReady = LookupReadyEnvelope & {
  registrationVariant: "member";
  optionCatalogs: MemberOptionCatalogs;
  /** FR-050: policy the member's "Create your password" field group renders. */
  passwordPolicy: Schemas["PasswordPolicyDescriptor"];
  /**
   * clm-web#238 V5: coarse server-resolved disposition of the member's
   * persisted credential attempts under ANY key. Routes the cold page
   * (Editing / recovery guidance / Support) and is never rendered, logged,
   * or persisted. Absent from servers predating the amendment → `none`.
   */
  memberCompletionState?: MemberCompletionState;
  /**
   * FR-028: canonical paths (each also present in `editableFields`) the
   * server declares REQUIRED for this lookup; today at most
   * `memberDetails.memberProgramSelection` (company not covered). Rendering
   * is governed by `editableFields` alone — a covered company omits the path
   * from BOTH arrays and nothing renders. Absent (servers predating the
   * amendment) → no server-declared required paths: an editable
   * coverage-gated path keeps its legacy optional rendering. Present and
   * listing a rendered path → the SPA renders the shared required marking
   * and blocks submission without a value.
   */
  requiredFields?: Schemas["MemberLookupReady"]["requiredFields"];
  prefill: PersonNamePrefill & {
    membershipCategory?: "standard" | "premium" | "mediator_arbitrator";
    sourceStatusCategory?: "invited" | "change_subscription" | "renew_subscription";
    practiceAreaReferences?: string[];
    arbitratorListing?: boolean;
    publicationPreferences?: Schemas["PublicationPreferences"];
  };
};

/** clm-web#238 V5 contract enum for `MemberLookupReady.memberCompletionState`. */
export type MemberCompletionState = NonNullable<
  Schemas["MemberLookupReady"]["memberCompletionState"]
>;

/**
 * Closed set of the contract's `memberCompletionState` values. `satisfies`
 * keeps this list in lockstep with the generated OpenAPI types: adding a
 * value to the spec without listing it here is a compile error, and a value
 * here that the spec dropped is one too.
 */
export const MEMBER_COMPLETION_STATES = [
  "none",
  "resubmittable",
  "recovery_available",
  "support_required",
] as const satisfies readonly MemberCompletionState[];

/**
 * UI contract 17a (fail closed): a PRESENT `memberCompletionState` outside the
 * contract enum must never route like `none`/absent — a renamed
 * `recovery_available` would otherwise enter Editing and mint a duplicate
 * credential attempt. Callers reject the payload instead.
 */
export function isMemberCompletionState(value: unknown): value is MemberCompletionState {
  return (
    typeof value === "string" && (MEMBER_COMPLETION_STATES as readonly string[]).includes(value)
  );
}

/**
 * The ONLY way `memberCompletionState` is read off a lookup payload (client
 * guard and page routing alike): an own-property read. An inherited value is
 * treated as absent (`undefined`), never as present-and-valid, because
 * JSON.parse never produces inherited properties — one can only come from a
 * non-wire object and must not route the cold page like `recovery_available`.
 */
export function readOwnMemberCompletionState(payload: object): unknown {
  return own(payload, "memberCompletionState");
}

/**
 * FR-028: the ONLY way `requiredFields` is read off a lookup payload. Own-
 * property read, accepted only as an array of strings; an inherited or
 * malformed value is treated as absent (`undefined`) — the legacy "no
 * server-declared required paths" rendering — never as present-and-valid.
 */
export function readOwnRequiredFields(payload: object): readonly string[] | undefined {
  const value = own(payload, "requiredFields");
  return Array.isArray(value) && value.every((entry) => typeof entry === "string")
    ? (value as string[])
    : undefined;
}

export type InvitationLookupReady = DirectFellowLookupReady | MemberLookupReady;

export type InvitationLookupResult =
  | InvitationLookupReady
  | PublicSafeOutcome
  | PublicInvalidRequest
  | RateLimitedResult
  | PublicDependencyUnavailable;

/**
 * Completion request shapes. The generated discriminator variants carry an
 * openapi-typescript mapping-name artifact that intersects conflicting
 * `registrationVariant` literals, reducing the generated variant types to
 * `never`. The variants are therefore recomposed from the generated
 * `CompletionRequestBase` plus the generated variant detail sub-schemas, so
 * every payload field still derives from the checked-in contract. Company/
 * location identity and the Direct Fellow staff-counsel office scope are
 * server-derived and have no request representation.
 */
export type MemberCompletionDetails = {
  companySizeReference?: string | null;
  practiceAreaReferences: string[];
  arbitratorListing: boolean;
  publicationPreferences: Schemas["PublicationPreferences"];
  /** Provider-neutral CLMS-117 selection; never card/payment/provider data.
   * Widened from the generated contract-pinned enum: the runtime authority
   * is the server's optionCatalogs.memberProgramSelections catalog. */
  memberProgramSelection?: string | null;
};

export type DirectFellowCompletionRequest = Schemas["CompletionRequestBase"] & {
  registrationVariant: "direct_fellow";
  fellowDetails: DirectFellowDetails;
};

/**
 * FR-050 member completion. The FIRST submission (`initial`) is the only
 * request that carries the password; every retry under the same idempotency
 * key is a `finalize` that carries only the proof and discriminators. The
 * password appears on the wire once per `initial`; a `resubmit_required`
 * outcome (server lost the pending credential) is the one case where the
 * user re-enters it and a new `initial` is sent under the same key.
 */
export type MemberInitialCompletionRequest = Schemas["CompletionRequestBase"] & {
  registrationVariant: "member";
  completionMode: "initial";
  memberDetails: MemberCompletionDetails;
  password: string;
};

export type MemberFinalizeCompletionRequest = Schemas["MemberFinalizeCompletionRequest"];

export type MemberCompletionRequest =
  | MemberInitialCompletionRequest
  | MemberFinalizeCompletionRequest;

export type RegistrationCompletionRequest = DirectFellowCompletionRequest | MemberCompletionRequest;

export type InvitationCompletionResult =
  | CompletionSucceeded
  | ValidationFailed
  | IdempotencyConflict
  | PublicSafeOutcome
  | PublicInvalidRequest
  | RateLimitedResult
  | DependencyUnavailableResult
  | DomainLegacyOwnedResult;

export class RegistrationInvitationApiError extends Error {
  readonly status: number;
  /**
   * Support reference from the rejected payload when the service supplied
   * one (fail-closed ready payloads carry it); absent for transport failures
   * and bodies without a discriminator.
   */
  readonly correlationId?: string;

  constructor(message: string, status: number, correlationId?: string) {
    super(message);
    this.name = "RegistrationInvitationApiError";
    this.status = status;
    if (correlationId !== undefined) {
      this.correlationId = correlationId;
    }
  }
}

export interface RegistrationInvitationClient {
  lookup(request: InvitationLookupRequest): Promise<InvitationLookupResult>;
  complete(
    request: RegistrationCompletionRequest,
    idempotencyKey: string,
  ): Promise<InvitationCompletionResult>;
}

export class HttpRegistrationInvitationClient implements RegistrationInvitationClient {
  private readonly fetcher: typeof fetch;

  constructor(fetcher: typeof fetch = globalThis.fetch) {
    // Bind so native fetch is not invoked with the client instance as `this`
    // (same "Illegal invocation" hazard handled in HttpAuthClient).
    this.fetcher = fetcher === globalThis.fetch ? fetcher.bind(globalThis) : fetcher;
  }

  async lookup(request: InvitationLookupRequest): Promise<InvitationLookupResult> {
    const result = await this.request<InvitationLookupResult>(
      "/public/registration-invitations/lookup",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      },
    );
    // FR-050 / UI contract L149: the member credential group renders the
    // ready lookup's `passwordPolicy`. A member `ready` without a usable
    // descriptor is unusable — entering the form would hide the credential
    // controls and submit an empty password (Copilot 3984492124) — so it
    // fails closed into the same generic error the caller already maps to
    // the unavailable terminal (UI contract L48/L85: missing required
    // ready-payload data → generic unavailable, never a partial form).
    if (
      result.outcome === "ready" &&
      result.registrationVariant === "member" &&
      !isPasswordPolicyDescriptor(own(result, "passwordPolicy"))
    ) {
      throw unusableReadyPayload(result);
    }
    // clm-web#238 V5 / UI contract 17a: the cold member `ready` routes on
    // `memberCompletionState`, and the page renders Editing for `none` and
    // for absence. A value outside the contract enum would therefore fall
    // through to the password form exactly like `none` (a `recovery_
    // available` renamed server-side would mint a duplicate credential
    // attempt), so a present-but-unrecognised state fails closed here into
    // the same generic unavailable error. Only ABSENCE is treated as `none`.
    // Read through the own-property helper: an inherited value is absent.
    const memberCompletionState =
      result.outcome === "ready" && result.registrationVariant === "member"
        ? readOwnMemberCompletionState(result)
        : undefined;
    if (memberCompletionState !== undefined && !isMemberCompletionState(memberCompletionState)) {
      throw unusableReadyPayload(result);
    }
    return result;
  }

  async complete(
    request: RegistrationCompletionRequest,
    idempotencyKey: string,
  ): Promise<InvitationCompletionResult> {
    return this.request<InvitationCompletionResult>(
      "/public/registration-invitations/complete",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(request),
      },
      // Completion only: a recognised 409 DOMAIN_LEGACY_OWNED is a deliberate
      // refusal, not an uncertain outcome. It is deliberately NOT published
      // to the staff write-authority host (this is the invitee's page).
      { outcome: "domain_legacy_owned" },
    );
  }

  /**
   * Every contract response — success or public-safe denial (400/409/422/
   * 429/503) — carries a JSON body with an `outcome` discriminator, so those
   * become typed results rather than exceptions. Anything else (network
   * failure, unexpected status) throws and is treated as an uncertain
   * outcome by the caller.
   */
  private async request<T>(
    path: `/${string}`,
    init: RequestInit,
    legacyOwnedResult?: T,
  ): Promise<T> {
    const response = await this.fetcher(this.url(path), {
      credentials: "include",
      ...init,
    });

    const body: unknown = await response.json().catch(() => undefined);

    if (legacyOwnedResult !== undefined && parseDomainLegacyOwned(response.status, body) !== null) {
      return legacyOwnedResult;
    }

    if (body && typeof body === "object" && Object.hasOwn(body, "outcome")) {
      const result = body as { outcome: unknown; retryAfterSeconds?: unknown };
      // `retryAfterSeconds` is a client-attached hint, never a contract body
      // property: a wire-supplied value would bypass the header bounds below
      // and reach the raw page timers unvalidated (clm-web#238 AA2).
      delete result.retryAfterSeconds;
      if (result.outcome === "rate_limited" || result.outcome === "dependency_unavailable") {
        // Strict delta-seconds only, bounded to [1, MAX_RETRY_AFTER_SECONDS]:
        // a lenient parseInt would accept `5abc`/`1e3` prefixes, and an
        // unbounded value would arm a raw `setTimeout(seconds * 1000)` that
        // overflows above 2147483s and fires immediately (premature finalize
        // re-send / instant 429 hold release). Anything outside the window
        // fails closed: no hint, so the manual Try-again / unheld paths own it.
        const retryAfterSeconds = parseRetryAfterSeconds(response.headers.get("Retry-After"));
        if (retryAfterSeconds !== null && retryAfterSeconds <= MAX_RETRY_AFTER_SECONDS) {
          result.retryAfterSeconds = retryAfterSeconds;
        }
      }
      return body as T;
    }

    // Deliberately generic: no invitation-existence detail is surfaced.
    throw new RegistrationInvitationApiError(
      `Registration invitation request failed with status ${response.status}.`,
      response.status,
    );
  }

  private url(path: `/${string}`): URL {
    // Resolved lazily per request so client construction is safe before the
    // runtime config is loaded. Public endpoints live on the standard realm.
    return new URL(path, getRuntimeConfig().authBaseUrl);
  }
}

/** Own-property read on an untrusted object; `undefined` when absent or inherited. */
function own(value: object, key: string): unknown {
  return Object.hasOwn(value, key) ? (value as Record<string, unknown>)[key] : undefined;
}

/**
 * A 200 `ready` payload the page cannot act on. Deliberately generic message;
 * the payload's own `correlationId` rides along so the unavailable terminal
 * can still offer a support reference for the failed lookup.
 */
function unusableReadyPayload(payload: object): RegistrationInvitationApiError {
  const correlationId = own(payload, "correlationId");
  return new RegistrationInvitationApiError(
    "Registration invitation lookup returned an unusable ready payload.",
    200,
    typeof correlationId === "string" ? correlationId : undefined,
  );
}
